import { useState, useEffect, useMemo } from "react";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Filter, 
  Download, 
  Upload,
  RefreshCw,
  Eye,
  EyeOff,
  ArrowUpDown,
  Calendar,
  DollarSign,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Split
} from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useBusiness } from "@/providers/business-provider";
import { useToast } from "@/hooks/use-toast";

// Transaction types for the register
const TRANSACTION_TYPES = [
  { value: "deposit", label: "Deposit", icon: DollarSign },
  { value: "transfer", label: "Transfer", icon: RefreshCw },
  { value: "payment", label: "Payment", icon: DollarSign },
  { value: "check", label: "Check", icon: FileText },
  { value: "expense", label: "Expense", icon: DollarSign },
  { value: "income", label: "Income", icon: DollarSign },
  { value: "journal", label: "Journal Entry", icon: FileText },
  { value: "invoice", label: "Invoice", icon: FileText },
  { value: "bill", label: "Bill", icon: FileText }
];

// Derive display type from transaction data (type field + description keywords)
function deriveDisplayType(transaction: any): string {
  const desc = (transaction.description || transaction.name || "").toLowerCase();
  const dbType = (transaction.type || "").toLowerCase();

  // Check description for specific patterns
  if (desc.includes("zelle") || desc.includes("venmo") || desc.includes("paypal") || desc.includes("transfer")) return "transfer";
  if (desc.includes("check ") || desc.match(/check\s*#?\d/)) return "check";
  if (desc.includes("atm")) return dbType === "income" ? "deposit" : "payment";

  // Fall back to DB type
  if (dbType === "income") return "deposit";
  if (dbType === "expense") return "payment";

  return dbType || "payment";
}

// Reconciliation statuses
const RECONCILIATION_STATUS = [
  { value: "unreconciled", label: "Unreconciled", color: "gray" },
  { value: "pending", label: "Pending", color: "yellow" },
  { value: "reconciled", label: "Reconciled", color: "green" },
  { value: "void", label: "Void", color: "red" }
];

interface Transaction {
  id: number;
  date: string;
  type: string;
  description: string;
  payee?: string;
  memo?: string;
  referenceNumber?: string;
  amount: string;
  categoryId?: number;
  categoryName?: string;
  accountId?: number;
  accountName?: string;
  reconciliationStatus: string;
  isReconciled: boolean;
  isSplit: boolean;
  splitEntries?: any[];
  businessId: number;
  createdAt: string;
  updatedAt: string;
}

interface Account {
  id: number;
  name: string;
  type: string;
  balance: string;
  accountNumber?: string;
}

interface RegisterFilters {
  startDate?: string;
  endDate?: string;
  type?: string;
  reconciliationStatus?: string;
  payee?: string;
  amountMin?: string;
  amountMax?: string;
  referenceNumber?: string;
  search?: string;
}

interface RegisterColumnConfig {
  date: boolean;
  type: boolean;
  refNumber: boolean;
  payee: boolean;
  memo: boolean;
  amount: boolean;
  balance: boolean;
  reconciled: boolean;
}

export default function TransactionRegisterPage() {
  const { activeBusiness } = useBusiness();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State management
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [filters, setFilters] = useState<RegisterFilters>({});
  const [sortBy, setSortBy] = useState<string>("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"standard" | "ledger">("standard");
  const [columnConfig, setColumnConfig] = useState<RegisterColumnConfig>({
    date: true,
    type: true,
    refNumber: true,
    payee: true,
    memo: true,
    amount: true,
    balance: true,
    reconciled: true
  });
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(new Set());
  const [showNewTransactionDialog, setShowNewTransactionDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);

  // Fetch real company accounts from database
  const { data: accounts = [], isLoading: accountsLoading } = useQuery<Account[]>({
    queryKey: [`/api/bank-register-accounts/${activeBusiness?.id}`],
    enabled: !!activeBusiness?.id,
  });

  // Fetch transactions for selected account
  const { data: transactions = [], isLoading: transactionsLoading, refetch: refetchTransactions } = useQuery({
    queryKey: [`/api/businesses/${activeBusiness?.id}/transactions`, { accountId: selectedAccountId, ...filters }],
    enabled: !!activeBusiness?.id && !!selectedAccountId,
    queryFn: async () => {
      if (!selectedAccountId || !activeBusiness?.id) return [];

      const params = new URLSearchParams();
      params.append('accountId', selectedAccountId.toString());
      params.append('limit', '500');
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });

      const url = `/api/businesses/${activeBusiness.id}/transactions?${params.toString()}`;
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) return [];
      const data = await response.json();
      // API returns { transactions, total } or array
      return data.transactions || data || [];
    }
  });

  // Calculate running balances
  const transactionsWithBalance = useMemo(() => {
    if (!transactions.length || !selectedAccountId) return [];
    
    const account = accounts.find((acc: Account) => acc.id === selectedAccountId);
    let runningBalance = parseFloat(account?.balance || "0");
    
    // Sort transactions by date for balance calculation
    const sortedTransactions = [...transactions].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    
    return sortedTransactions.map(transaction => {
      const amount = parseFloat(transaction.amount);
      
      // Adjust balance based on transaction type
      if (transaction.type === "income" || transaction.type === "deposit") {
        runningBalance += amount;
      } else {
        runningBalance -= amount;
      }
      
      return {
        ...transaction,
        runningBalance: runningBalance.toFixed(2)
      };
    }).reverse(); // Show most recent first
  }, [transactions, selectedAccountId, accounts]);

  // Filtered and sorted transactions
  const filteredTransactions = useMemo(() => {
    let filtered = transactionsWithBalance;

    // Apply filters
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(t => 
        t.description.toLowerCase().includes(searchTerm) ||
        t.payee?.toLowerCase().includes(searchTerm) ||
        t.memo?.toLowerCase().includes(searchTerm) ||
        t.referenceNumber?.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.startDate) {
      filtered = filtered.filter(t => new Date(t.date) >= new Date(filters.startDate!));
    }

    if (filters.endDate) {
      filtered = filtered.filter(t => new Date(t.date) <= new Date(filters.endDate!));
    }

    if (filters.type) {
      filtered = filtered.filter(t => t.type === filters.type);
    }

    if (filters.reconciliationStatus) {
      filtered = filtered.filter(t => t.reconciliationStatus === filters.reconciliationStatus);
    }

    if (filters.payee) {
      filtered = filtered.filter(t => t.payee?.toLowerCase().includes(filters.payee!.toLowerCase()));
    }

    if (filters.amountMin) {
      filtered = filtered.filter(t => parseFloat(t.amount) >= parseFloat(filters.amountMin!));
    }

    if (filters.amountMax) {
      filtered = filtered.filter(t => parseFloat(t.amount) <= parseFloat(filters.amountMax!));
    }

    if (filters.referenceNumber) {
      filtered = filtered.filter(t => 
        t.referenceNumber?.toLowerCase().includes(filters.referenceNumber!.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy as keyof Transaction];
      let bValue: any = b[sortBy as keyof Transaction];

      if (sortBy === "amount" || sortBy === "runningBalance") {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (sortBy === "date") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [transactionsWithBalance, filters, sortBy, sortOrder]);

  // Mutations for transaction operations
  const createTransactionMutation = useMutation({
    mutationFn: (transactionData: any) => 
      apiRequest("POST", `/api/businesses/${activeBusiness?.id}/transactions`, transactionData),
    onSuccess: () => {
      toast({ title: "Success", description: "Transaction created successfully" });
      refetchTransactions();
      setShowNewTransactionDialog(false);
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to create transaction",
        variant: "destructive" 
      });
    }
  });

  const updateTransactionMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      apiRequest("PATCH", `/api/businesses/${activeBusiness?.id}/transactions/${id}`, data),
    onSuccess: () => {
      toast({ title: "Success", description: "Transaction updated successfully" });
      refetchTransactions();
      setEditingTransaction(null);
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to update transaction",
        variant: "destructive" 
      });
    }
  });

  const deleteTransactionMutation = useMutation({
    mutationFn: (id: number) => 
      apiRequest("DELETE", `/api/businesses/${activeBusiness?.id}/transactions/${id}`),
    onSuccess: () => {
      toast({ title: "Success", description: "Transaction deleted successfully" });
      refetchTransactions();
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to delete transaction",
        variant: "destructive" 
      });
    }
  });

  const reconcileTransactionsMutation = useMutation({
    mutationFn: (transactionIds: number[]) => 
      apiRequest("PATCH", `/api/businesses/${activeBusiness?.id}/transactions/reconcile`, { 
        transactionIds, 
        status: "reconciled" 
      }),
    onSuccess: () => {
      toast({ title: "Success", description: "Transactions reconciled successfully" });
      refetchTransactions();
      setSelectedTransactions(new Set());
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to reconcile transactions",
        variant: "destructive" 
      });
    }
  });

  // Helper functions
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleSelectTransaction = (transactionId: number) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSelectAllTransactions = () => {
    if (selectedTransactions.size === filteredTransactions.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  const getTransactionTypeInfo = (type: string) => {
    return TRANSACTION_TYPES.find(t => t.value === type) || TRANSACTION_TYPES.find(t => t.value === "payment")!;
  };

  const getReconciliationStatusInfo = (status: string) => {
    return RECONCILIATION_STATUS.find(s => s.value === status) || RECONCILIATION_STATUS[0];
  };

  const selectedAccount = accounts.find((acc: Account) => acc.id === selectedAccountId);

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Bank Register</h1>
            <p className="text-gray-600 mt-2">
              Comprehensive account transaction management and reconciliation
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
            <Button
              onClick={() => setShowNewTransactionDialog(true)}
              disabled={!selectedAccountId}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Transaction
            </Button>
          </div>
        </div>

        {/* Account Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Account Selection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="account-select">Select Account</Label>
                <Select
                  value={selectedAccountId?.toString() || ""}
                  onValueChange={(value) => {
                    const accountId = parseInt(value);
                    console.log('Account selected:', accountId);
                    setSelectedAccountId(accountId);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an account..." />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((account: Account) => (
                      <SelectItem key={account.id} value={account.id.toString()}>
                        {account.name} - {formatCurrency(parseFloat(account.balance))}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedAccount && (
                <>
                  <div>
                    <Label>Account Type</Label>
                    <div className="mt-2">
                      <Badge variant="outline">{selectedAccount.type}</Badge>
                    </div>
                  </div>
                  <div>
                    <Label>Current Balance</Label>
                    <div className="mt-2 text-xl font-semibold">
                      {formatCurrency(parseFloat(selectedAccount.balance))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Filters Panel */}
        {showFilters && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Transaction Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="search">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="search"
                      placeholder="Search transactions..."
                      className="pl-9"
                      value={filters.search || ""}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={filters.startDate || ""}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={filters.endDate || ""}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="transaction-type">Transaction Type</Label>
                  <Select
                    value={filters.type || "__all__"}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, type: value === "__all__" ? undefined : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All types</SelectItem>
                      {TRANSACTION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="reconciliation-status">Reconciliation Status</Label>
                  <Select
                    value={filters.reconciliationStatus || "__all__"}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, reconciliationStatus: value === "__all__" ? undefined : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All statuses</SelectItem>
                      {RECONCILIATION_STATUS.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="payee">Payee</Label>
                  <Input
                    id="payee"
                    placeholder="Filter by payee..."
                    value={filters.payee || ""}
                    onChange={(e) => setFilters(prev => ({ ...prev, payee: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="amount-min">Min Amount</Label>
                  <Input
                    id="amount-min"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={filters.amountMin || ""}
                    onChange={(e) => setFilters(prev => ({ ...prev, amountMin: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="amount-max">Max Amount</Label>
                  <Input
                    id="amount-max"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={filters.amountMax || ""}
                    onChange={(e) => setFilters(prev => ({ ...prev, amountMax: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setFilters({})}
                  disabled={Object.keys(filters).length === 0}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setViewMode(viewMode === "standard" ? "ledger" : "standard")}
                >
                  {viewMode === "standard" ? "Ledger View" : "Standard View"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bank Register */}
        {selectedAccountId && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Bank Register - {selectedAccount?.name}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {selectedTransactions.size > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => reconcileTransactionsMutation.mutate(Array.from(selectedTransactions))}
                        disabled={reconcileTransactionsMutation.isPending}
                        className="flex items-center gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Reconcile Selected ({selectedTransactions.size})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedTransactions(new Set())}
                        className="flex items-center gap-2"
                      >
                        <XCircle className="h-4 w-4" />
                        Clear Selection
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchTransactions()}
                    disabled={transactionsLoading}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${transactionsLoading ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin" />
                  <span className="ml-2">Loading transactions...</span>
                </div>
              ) : filteredTransactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No transactions found for the selected criteria.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">
                          <Checkbox
                            checked={selectedTransactions.size === filteredTransactions.length}
                            onCheckedChange={handleSelectAllTransactions}
                          />
                        </TableHead>
                        {columnConfig.date && (
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => handleSort("date")}
                          >
                            <div className="flex items-center gap-1">
                              Date
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </TableHead>
                        )}
                        {columnConfig.type && (
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => handleSort("type")}
                          >
                            <div className="flex items-center gap-1">
                              Type
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </TableHead>
                        )}
                        {columnConfig.refNumber && (
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => handleSort("referenceNumber")}
                          >
                            <div className="flex items-center gap-1">
                              Ref #
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </TableHead>
                        )}
                        {columnConfig.payee && (
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => handleSort("payee")}
                          >
                            <div className="flex items-center gap-1">
                              Payee
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </TableHead>
                        )}
                        {columnConfig.memo && (
                          <TableHead>Memo/Description</TableHead>
                        )}
                        {columnConfig.amount && (
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-50 text-right"
                            onClick={() => handleSort("amount")}
                          >
                            <div className="flex items-center justify-end gap-1">
                              Amount
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </TableHead>
                        )}
                        {columnConfig.balance && (
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-50 text-right"
                            onClick={() => handleSort("runningBalance")}
                          >
                            <div className="flex items-center justify-end gap-1">
                              Balance
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </TableHead>
                        )}
                        {columnConfig.reconciled && (
                          <TableHead className="text-center">Status</TableHead>
                        )}
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTransactions.map((transaction: any) => {
                        const typeInfo = getTransactionTypeInfo(deriveDisplayType(transaction));
                        const statusInfo = getReconciliationStatusInfo(transaction.reconciliationStatus);
                        const TypeIcon = typeInfo.icon;

                        return (
                          <TableRow key={transaction.id} className={`
                            ${selectedTransactions.has(transaction.id) ? "bg-blue-50" : ""}
                            ${transaction.reconciliationStatus === "void" ? "opacity-60 line-through" : ""}
                            hover:bg-gray-50
                          `}>
                            <TableCell>
                              <Checkbox
                                checked={selectedTransactions.has(transaction.id)}
                                onCheckedChange={() => handleSelectTransaction(transaction.id)}
                              />
                            </TableCell>
                            {columnConfig.date && (
                              <TableCell>{formatDate(transaction.date)}</TableCell>
                            )}
                            {columnConfig.type && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <TypeIcon className="h-4 w-4" />
                                  <span className="text-sm">{typeInfo.label}</span>
                                </div>
                              </TableCell>
                            )}
                            {columnConfig.refNumber && (
                              <TableCell className="font-mono text-sm">
                                {transaction.referenceNumber || "-"}
                              </TableCell>
                            )}
                            {columnConfig.payee && (
                              <TableCell>{transaction.payee || "-"}</TableCell>
                            )}
                            {columnConfig.memo && (
                              <TableCell className="max-w-[200px] truncate">
                                {transaction.memo || transaction.description}
                                {transaction.isSplit && (
                                  <Badge variant="outline" className="ml-2">
                                    <Split className="h-3 w-3 mr-1" />
                                    Split
                                  </Badge>
                                )}
                              </TableCell>
                            )}
                            {columnConfig.amount && (
                              <TableCell className="text-right font-mono">
                                <span className={`
                                  ${transaction.type === "income" || transaction.type === "deposit" 
                                    ? "text-green-600" 
                                    : "text-red-600"}
                                `}>
                                  {transaction.type === "income" || transaction.type === "deposit" ? "+" : "-"}
                                  {formatCurrency(Math.abs(parseFloat(transaction.amount || "0")))}
                                </span>
                              </TableCell>
                            )}
                            {columnConfig.balance && (
                              <TableCell className="text-right font-mono font-semibold">
                                {formatCurrency(parseFloat(transaction.runningBalance || transaction.balance || "0"))}
                              </TableCell>
                            )}
                            {columnConfig.reconciled && (
                              <TableCell className="text-center">
                                {transaction.reconciliationStatus === "reconciled" || transaction.isReconciled ? (
                                  <div className="flex items-center justify-center">
                                    <span className="font-bold text-green-700 text-lg">C</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center">
                                    <span className="text-gray-400">-</span>
                                  </div>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Transaction summary */}
        {selectedAccountId && filteredTransactions.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{filteredTransactions.length}</div>
                  <div className="text-sm text-gray-600">Total Transactions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(
                      filteredTransactions
                        .filter(t => t.type === "income" || t.type === "deposit")
                        .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                    )}
                  </div>
                  <div className="text-sm text-gray-600">Total Income</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {formatCurrency(
                      filteredTransactions
                        .filter(t => t.type !== "income" && t.type !== "deposit")
                        .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                    )}
                  </div>
                  <div className="text-sm text-gray-600">Total Expenses</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {Math.round((filteredTransactions.filter(t => t.isReconciled).length / filteredTransactions.length) * 100)}%
                  </div>
                  <div className="text-sm text-gray-600">Reconciled</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* New Transaction Dialog */}
      {/* This would be implemented as a separate component */}
      
      {/* Edit Transaction Dialog */}
      {/* This would be implemented as a separate component */}
    </Layout>
  );
}
